import { ArrowLeft, Check, Download, FileEdit } from "lucide-react";
import type { Chapter } from "../data";
import { type ExportFormat, type ProjectInfo, exportConversation } from "../chatClient";
import { TypingDots } from "./TypingDots";
import { ChapterStatusItem } from "./ChapterStatusItem";

export function Sidebar({
  analysisDone,
  analyzing,
  projectInfo,
  chapters,
  completedChapters,
  started,
  chapterIndex,
  docsEnabled,
  allChaptersDone,
  exportFormat,
  setExportFormat,
  exportEnabled,
  activeConversationId,
  onOpenChapterDoc,
  onOpenFullDoc,
  onBackToLanding,
}: {
  analysisDone: boolean;
  analyzing: boolean;
  projectInfo: ProjectInfo | null;
  chapters: Chapter[];
  completedChapters: string[];
  started: boolean;
  chapterIndex: number;
  docsEnabled: boolean;
  allChaptersDone: boolean;
  exportFormat: ExportFormat;
  setExportFormat: (f: ExportFormat) => void;
  exportEnabled: boolean;
  activeConversationId: string | null;
  onOpenChapterDoc: (chapter: Chapter) => void;
  onOpenFullDoc: () => void;
  onBackToLanding: () => void;
}) {
  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <span className="logo-mark">临床方案平台</span>
        <p className="sidebar-sub">ICH 临床试验方案 · 章节生成</p>
      </div>

      <div className="sidebar-section">
        <h3 className="sidebar-title">当前项目</h3>
        {analysisDone && projectInfo ? (
          <div className="project-card">
            <span className="badge">{projectInfo.badge}</span>
            <p className="project-name">{projectInfo.name}</p>
          </div>
        ) : analysisDone ? (
          <div className="project-card">
            <p className="project-name">会话已就绪</p>
          </div>
        ) : (
          <div className="project-card project-card-loading">
            <TypingDots />
            <p className="project-name project-name-loading">
              {analyzing ? "正在分析项目信息…" : "等待首轮对话完成…"}
            </p>
          </div>
        )}
      </div>

      <div className="sidebar-section chapters">
        <h3 className="sidebar-title">会话状态</h3>
        <ol className="chapter-list">
          <ChapterStatusItem
            label={analysisDone ? "已分析当前项目信息" : "正在分析当前项目信息"}
            state={analysisDone ? "done" : "current"}
          />
          {chapters.map((chapter, index) => (
            <ChapterStatusItem
              key={chapter.num}
              label={
                <>
                  <span className="chapter-num">{chapter.num}</span> {chapter.title}
                </>
              }
              state={
                // 有正文 = 已完成（最后一章同样适用）；章节完成状态不依赖项目信息（badge/name）是否提取成功
                completedChapters.includes(chapter.num)
                  ? "done"
                  : started && index === chapterIndex
                  ? "current"
                  : "pending"
              }
              // 未生成正文的章节也允许点开：弹窗里还有「提示词」页可读、可批注
              onClick={activeConversationId ? () => onOpenChapterDoc(chapter) : undefined}
              title={
                !activeConversationId
                  ? "会话就绪后即可查看本章"
                  : completedChapters.includes(chapter.num)
                  ? "预览 / 批注本章正文与提示词"
                  : "本章尚未生成正文，可先查看 / 批注提示词"
              }
            />
          ))}
        </ol>
        {allChaptersDone && (
          <div className="chapters-complete" role="status">
            <Check size={14} className="icon" />
            <span>全部 {chapters.length} 个章节已完成，可导出完整方案</span>
          </div>
        )}
      </div>

      <div className="sidebar-footer">
        <button
          className="btn btn-outlined-dark"
          type="button"
          disabled={!docsEnabled}
          title={docsEnabled ? "" : "发送第一条消息后即可预览与批注方案文档"}
          onClick={onOpenFullDoc}
        >
          <FileEdit size={14} className="icon" />
          <span>完整文档预览 / 批注</span>
        </button>
        <div className="export-group">
          <label className="export-format-label" htmlFor="export-format">
            导出格式
          </label>
          <select
            id="export-format"
            className="form-input export-format-select"
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
          >
            <option value="docx">Word (.docx)</option>
            <option value="pdf">PDF</option>
            <option value="markdown">Markdown</option>
          </select>
          <button
            className="btn btn-outlined-dark"
            type="button"
            disabled={!exportEnabled}
            title={exportEnabled ? "" : "还没有已完成的章节可导出"}
            onClick={() => activeConversationId && exportConversation(activeConversationId, exportFormat)}
          >
            <Download size={16} className="icon" />
            <span>{exportEnabled ? `导出已完成章节（${completedChapters.length}）` : "导出已完成章节"}</span>
          </button>
        </div>
        <button className="btn btn-outlined-dark" type="button" onClick={onBackToLanding}>
          <ArrowLeft size={16} className="icon" />
          <span>返回</span>
        </button>
      </div>
    </aside>
  );
}
